import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { FullLoaderService } from 'src/app/shared/services/full-loader.service';
import { QuoteService } from '../../dashboard/quote/quote.service';
import { DatatableComponent } from '@swimlane/ngx-datatable';
import { ServiceRfqTokenAuthService } from 'src/app/shared/services/service-rfq-token-auth.service';
import { SupplierService } from 'src/app/views/settings/suppliers/supplier.service';
import moment from 'moment';

@Component({
  // Issue 7 — force Material form-field labels on this screen to float to
  // the top so they never rest over / overlap the input value.
  providers: [
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { floatLabel: 'always' } },
  ],
  selector: 'app-service-rfq-token-details',
  templateUrl: './serviceRfqDetails.component.html',
  styleUrls: ['./serviceRfqDetails.component.css'],
})
export class ServiceRfqTokenDetailsComponent implements OnInit {
  @ViewChild('supplierTable') table!: DatatableComponent;
  showLoader: boolean = false;
  showDetailSection: boolean = false;
  isWrapDescription: boolean = false;
  isForm1Expand: boolean = true;
  isForm2Expand: boolean = true;
  isForm3Expand: boolean = true;
  isForm4Expand: boolean = true;
  isForm5Expand: boolean = true;
  isForm6Expand: boolean = true;
  isForm7Expand: boolean = true;
  isForm8Expand: boolean = true;
  isForm9Expand: boolean = true;
  isForm10Expand: boolean = true;
  isForm11Expand: boolean = true;

  serviceRfqId!: number;
  lineItemData: any[] = [];
  lineItemDataAttachment: any[] = [];
  headerData: any;

  rfqattachmentSection: boolean = false;
  rfqattachmentImages: any[] = [];
  currentIndex = 0;

  buyerAttachmentSection: boolean = false;
  buyerAttachmentUrl: any[] = [];

  isCollapsed: boolean = false;
  buyerHeaderAttachmentSection: boolean = false;
  buyerHeaderAttachmentUrl: any;

  declineReason: string = '';
  showDeclineConfirmationDialogue: boolean = false;

  tempFormGroup!: UntypedFormGroup;
  quoteId: any = null;

  constructor(
    private router: Router,
    private snack: MatSnackBar,
    private activatedRoute: ActivatedRoute,
    private fullLoaderService: FullLoaderService,
    private quoteService: QuoteService,
    private supplierService: SupplierService,
    private serviceRfqTokenAuth: ServiceRfqTokenAuthService,
    private _formBuilder: UntypedFormBuilder,
  ) {}

  toggleBlur(): void {
    const element = document.querySelector('.blurry');
    if (element) {
      element.classList.toggle('blur');
    }
  }

  async toggleRfqAttachmentImage(value: boolean, row?: any) {
    if (row) {
      try {
        await this.fetchServiceRfqDetailsAttachments(
          row.serviceRfqLineItemDetailsId
        );

        if (
          this.lineItemDataAttachment.length > 0 &&
          Array.isArray(this.lineItemDataAttachment)
        ) {
          this.rfqattachmentImages = [];
          this.lineItemDataAttachment.forEach((item) => {
            if (item.s3Path) {
              this.rfqattachmentImages.push(item.s3Path);
            }
          });
        } else {
          this.rfqattachmentImages = [
            'https://ihm-assets-dev.s3-eu-west-1.amazonaws.com/no-image-available-grid.jpg',
          ];
        }
      } catch (error) {
        console.error('Error fetching service RFQ attachment data:', error);
        this.rfqattachmentImages = [];
      }
    }
    this.toggleBlur();
    this.rfqattachmentSection = value;
  }

  toggleBuyerAttachmentImage(value: boolean) {
    this.buyerAttachmentSection = value;
  }

  async toggleBuyerHeaderAttachmentImage(value: boolean, url?: any) {
    if (url) {
      this.buyerHeaderAttachmentUrl = url;
    }
    this.toggleBlur();
    this.buyerHeaderAttachmentSection = value;
  }

  nextSlide() {
    this.currentIndex =
      (this.currentIndex + 1) % this.rfqattachmentImages.length;
  }

  prevSlide() {
    this.currentIndex =
      (this.currentIndex - 1 + this.rfqattachmentImages.length) %
      this.rfqattachmentImages.length;
  }

  isPdf(url: string): boolean {
    return url?.toLowerCase().endsWith('.pdf');
  }

  isImage(url: string): boolean {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.svg'];
    return imageExtensions.some((ext) => url.toLowerCase().endsWith(ext));
  }

  async ngOnInit() {
    // Validate token session
    const token = this.serviceRfqTokenAuth.getToken();
    if (!token) {
      this.snack.open('Session expired. Please re-enter your Service RFQ token.', 'OK', {
        duration: 4000,
      });
      this.router.navigate(['/serviceTokenLogin']);
      return;
    }

    this.fullLoaderService.showLoader();
    this.serviceRfqId = this.activatedRoute.snapshot.params['serviceRfqId'];

    const supplierId = this.serviceRfqTokenAuth.getSupplierId();

    // Build tempFormGroup for service quote header
    this.tempFormGroup = this._formBuilder.group({
      quoteId: [null],
      serviceRfqId: [null],
      quoteRefNumber: [null],
      serviceSupplierRfqDetailsId: [null],
      quoteDate: [new Date()],
      priceGoodUntilDate: [null],
      offeredQuality: [null],
      onTimeDeliveryOrderDate: [null],
      quotedDeliveryInDays: [null],
      incoterms: [null],
      incotermLocation: [null],
      supplierId: [null],
      supplierContactName: [''],
      supplierEmail: [null],
      supplierTelephone: [null],
      supplierMobile: [null],
      supplierCompanyEmail: [null],
      supplierCurrency: [null],
      supplierComment: [null],
      buyerComment: [null],
      quoteHeaderAttachmentType: [null],
      quoteHeaderAttachments: [null],
      quoteFreightForwarderId: [null],
      freightForwarderDetailsId: [null],
      freightForwarderCompanyName: [null],
      pic: [null],
      freightForwarderJobTitle: [null],
      freightForwarderEmailId: [''],
      freightForwarderPhone: [null],
      freightForwarderFax: [null],
      freightForwarderAddress: [null],
      packaging: [null],
      modeOfTransport: [null],
      paymentTermsId: [null],
      termsAndConditions: [null],
      currencyInstructions: [null],
    });

    if (supplierId) {
      // Format dates
      const dateString1 = this.tempFormGroup.value.quoteDate
        ? moment(this.tempFormGroup.value.quoteDate).format('YYYY-MM-DDTHH:mm:ss') + 'Z'
        : null;

      this.tempFormGroup.patchValue({
        quoteDate: dateString1,
        serviceRfqId: this.serviceRfqId,
        supplierId: supplierId,
      });

      // Get last service quote ID for ref number generation
      try {
        const data = await this.quoteService.getLastServiceQuoteIdByServiceRfqToken();
        const lastId = data[0]?.lastId || 0;
        this.tempFormGroup.patchValue({
          quoteRefNumber:
            'SQ-VSMPS-' +
            new Date().getFullYear().toString() +
            String(new Date().getMonth() + 1).padStart(2, '0') +
            String(new Date().getDate()).padStart(2, '0') +
            '-' +
            (lastId + 1),
        });
      } catch (err) {
        console.error('Error getting last service quote id:', err);
      }
    }

    await this.fetchServiceRfqDetails();
    this.fullLoaderService.hideLoader();
  }

  async fetchServiceRfqDetailsAttachments(lineItemDetailId: any) {
    this.fullLoaderService.showLoader();
    let params = {
      serviceRfqId: this.serviceRfqId,
      serviceRfqLineItemDetailId: lineItemDetailId,
    };
    const data = await this.quoteService.fetchServiceRfqLineItemAttachmentsByServiceRfqToken(params);
    console.log('service rfq line item attachments:', data);
    if (data.lineItemData && data.lineItemData.length > 0) {
      const transformedData: any[] = [];
      data.lineItemData.forEach((item: any) => {
        transformedData.push({
          s3Path: item.s3Path,
        });
      });
      this.lineItemDataAttachment = transformedData;
    } else {
      this.lineItemDataAttachment = [];
    }
    this.fullLoaderService.hideLoader();
  }

  async fetchServiceRfqDetails() {
    let params = {
      serviceRfqId: this.serviceRfqId,
    };
    const data = await this.quoteService.fetchServiceRfqDetailsByServiceRfqToken(params);
    console.log('service rfq details data->', data);

    if (data.lineItemData && data.lineItemData.length > 0) {
      const transformedData: any[] = [];
      data.lineItemData.forEach((item: any, index: number) => {
        transformedData.push({
          serialNo: (index + 1).toString(),
          serviceName: item.serviceName,
          serviceDescription: item.serviceDescription,
          scopeOfWork: item.scopeOfWork || '-',
          uom: item.uom,
          quantity: item.quantity,
          serviceRfqLineItemDetailsId: item.serviceRfqLineItemDetailsId,
        });
      });
      this.lineItemData = transformedData;
    }

    this.headerData = data.rfqData[0];
    if (this.headerData) {
      this.headerData.serviceRfqId = this.serviceRfqId;

      // Patch serviceSupplierRfqDetailsId into tempFormGroup for saveAsDraft
      if (this.tempFormGroup && this.headerData.supplierRfqId) {
        this.tempFormGroup.patchValue({
          serviceSupplierRfqDetailsId: this.headerData.supplierRfqId,
        });
      }

      // Nest vessel data
      this.headerData.vessel = {
        vesselName: this.headerData.vesselName,
        imoNumber: this.headerData.vesselImoNumber,
        typeOfVessel: this.headerData.vesselType,
        class: this.headerData.vesselClass,
      };

      // Parse buyerAttachments
      let buyerAttachments = this.headerData.buyerAttachments;
      if (typeof buyerAttachments === 'string') {
        try {
          buyerAttachments = JSON.parse(buyerAttachments);
        } catch (e) {
          buyerAttachments = [];
        }
      }
      if (!buyerAttachments || !Array.isArray(buyerAttachments)) {
        buyerAttachments = [];
      }

      this.buyerAttachmentUrl = [];
      buyerAttachments.forEach((attachment: any) => {
        if (attachment.s3_path && attachment.name) {
          this.buyerAttachmentUrl.push({
            name: attachment.name,
            s3_path: attachment.s3_path,
          });
        }
      });
    }
  }

  toggleVisibility(section: string) {
    if (section == 'form1') {
      this.isForm1Expand = !this.isForm1Expand;
    } else if (section == 'form2') {
      this.isForm2Expand = !this.isForm2Expand;
    } else if (section == 'form3') {
      this.isForm3Expand = !this.isForm3Expand;
    } else if (section == 'form4') {
      this.isForm4Expand = !this.isForm4Expand;
    } else if (section == 'form5') {
      this.isForm5Expand = !this.isForm5Expand;
    } else if (section == 'form6') {
      this.isForm6Expand = !this.isForm6Expand;
    } else if (section == 'form7') {
      this.isForm7Expand = !this.isForm7Expand;
    } else if (section == 'form8') {
      this.isForm8Expand = !this.isForm8Expand;
    } else if (section == 'form9') {
      this.isForm9Expand = !this.isForm9Expand;
    } else if (section == 'form10') {
      this.isForm10Expand = !this.isForm10Expand;
    } else if (section == 'form11') {
      this.isForm11Expand = !this.isForm11Expand;
    }
  }

  toggleShowHeaderDetails(event: { checked: boolean }) {
    this.showDetailSection = event.checked;
    setTimeout(() => {
      if (this.table) {
        this.table.recalculate();
      }
    }, 100);
  }

  toggleWrapDescription(event: MatSlideToggleChange) {
    this.isWrapDescription = event.checked;
  }

  back() {
    this.serviceRfqTokenAuth.clear();
    this.router.navigate(['/serviceTokenLogin']);
  }

  // =============================================
  // Action button methods
  // =============================================
  async rfqActions(action: string) {
    if (action == 'download') {
      this.fullLoaderService.showLoader();
      let fileName = this.headerData?.rfqRefNumber + '.pdf';
      const supplierId = Number(this.serviceRfqTokenAuth.getSupplierId());
      const pdf = await this.quoteService.generateSupplierServiceRfqAttachmentByServiceRfqToken(
        this.serviceRfqId,
        supplierId
      );
      const uint8Array = new Uint8Array(pdf.data);
      const blob = new Blob([uint8Array], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      this.fullLoaderService.hideLoader();
    } else if (action == 'decline') {
      this.showDeclineConfirmationDialogue = true;
    } else if (action == 'acknowledge') {
      this.fullLoaderService.showLoader();

      let params = {
        serviceRfqId: this.serviceRfqId,
        buyerId: this.headerData?.buyerId,
        rfqNumber: this.headerData?.rfqRefNumber,
        vesselName: this.headerData?.vessel?.vesselName,
      };

      try {
        await this.quoteService.acknowledgeServiceRfqByServiceRfqToken(params);
        this.fullLoaderService.hideLoader();
        this.snack.open('Service RFQ has been acknowledged', 'OK', {
          duration: 3000,
        });
        await this.fetchServiceRfqDetails();
      } catch (error) {
        this.fullLoaderService.hideLoader();
        this.snack.open('Failed to acknowledge Service RFQ', 'OK', {
          duration: 3000,
        });
        console.error('Error acknowledging Service RFQ:', error);
      }
    }
  }

  async createQuote() {
    this.fullLoaderService.showLoader();
    try {
      const params1 = {
        serviceRfqId: this.serviceRfqId,
      };

      // Step 1: Check if a service quote already exists
      const quoteData = await this.quoteService.getServiceQuoteIdByServiceRfqToken(params1);
      console.log('getServiceQuoteId result:', quoteData);

      if (quoteData && quoteData.length > 0 && quoteData[0]?.quoteId) {
        this.quoteId = quoteData[0].quoteId;
        this.headerData.quoteId = this.quoteId;
        this.fullLoaderService.hideLoader();
        this.snack.open('Existing quotation found. Navigating to quote.', 'OK', { duration: 3000 });
        this.router.navigate(['/serviceRfqToken/quote-line-items'], {
          queryParams: { serviceRfqId: this.serviceRfqId, quoteId: this.quoteId },
        });
      } else {
        // Step 2: No quote exists - create a new draft
        const params2 = {
          section: 'tab1',
          quoteStatus: 14,
          tabId: 1,
          headerDetails: this.tempFormGroup.value,
        };

        const result = await this.quoteService.saveServiceQuoteAsDraftByServiceRfqToken(params2);
        if (result?.data) {
          this.quoteId = result.data;
          this.headerData.quoteId = this.quoteId;
          this.fullLoaderService.hideLoader();
          this.snack.open('Service Quotation initiated', 'OK', { duration: 3000 });
          this.router.navigate(['/serviceRfqToken/quote-line-items'], {
            queryParams: { serviceRfqId: this.serviceRfqId, quoteId: this.quoteId },
          });
        } else {
          throw new Error('Invalid response format');
        }
      }
    } catch (error) {
      this.fullLoaderService.hideLoader();
      this.snack.open('Failed to initiate Service Quotation', 'OK', { duration: 3000 });
      console.error('Error in createQuote:', error);
    }
  }

  cancelDecline() {
    this.showDeclineConfirmationDialogue = false;
    this.declineReason = '';
  }

  async declineRfq() {
    this.fullLoaderService.showLoader();

    let params = {
      serviceRfqId: this.serviceRfqId,
      declineReason: this.declineReason,
      buyerId: this.headerData?.buyerId,
      rfqNumber: this.headerData?.rfqRefNumber,
      vesselName: this.headerData?.vessel?.vesselName,
    };

    try {
      await this.quoteService.declineServiceRfqByServiceRfqToken(params);
      this.fullLoaderService.hideLoader();
      this.showDeclineConfirmationDialogue = false;
      this.snack.open('Service RFQ has been declined', 'OK', {
        duration: 3000,
      });
      await this.fetchServiceRfqDetails();
    } catch (error) {
      this.fullLoaderService.hideLoader();
      this.showDeclineConfirmationDialogue = false;
      this.snack.open('Failed to decline Service RFQ', 'OK', {
        duration: 3000,
      });
      console.error('Error declining Service RFQ:', error);
    }
  }

  async generateRFQExcelForSupplier() {
    this.fullLoaderService.showLoader('Loading RFQ data, please wait...')
    
    if(!this.headerData?.supplierId || !this.headerData?.rfqRefNumber || !this.serviceRfqId){
      this.snack.open('File can not be downloaded', 'OK', {
        duration: 3000,
      });
      return;
    }
    
    try {
      const result = await this.quoteService.fetchServiceRfqDetailsForExcelByServiceRfqToken(
        this.serviceRfqId,
        this.headerData?.supplierId
      );

      if (!result || !result.rfqData || result.rfqData.length === 0) {
        this.snack.open('No data found for this Service RFQ.', 'OK', { duration: 3000 });
        return;
      }

      const refNo = this.headerData?.rfqRefNumber

      const fileName = refNo + '.xlsx';

      const labelMap: { [key: string]: string } = {
        name: 'Name',
        address: 'Address',
        contact: 'Contact',
        phone: 'Phone',
        email: 'Email',
        mpsBuyerCode: 'MPS Buyer Code',
        mpsSupplierCode: 'MPS Supplier Code',
        PIC: 'PIC',
        fax: 'Fax',
        portName: 'Port Name',
        vesselETA: 'Vessel ETA',
        vesselETD: 'Vessel ETD',
      };

      const rfqData = result.rfqData[0];
      const lineItemData = result.lineItemData || [];

      // Helper to format ISO date strings to DD/MM/YYYY
      const formatDate = (val: any) => {
        if (!val) return '';
        const d = moment(val);
        return d.isValid() ? d.format('DD/MM/YYYY') : '';
      };

      const data = {
        rfqRef: rfqData.serviceRfqRefNumber || '',
        rfqDate: formatDate(rfqData.serviceRfqDate),
        preferredCurrency: rfqData.currencyCode || '',
        vesselName: rfqData.vesselName || '',
        imoNumber: rfqData.vesselImoNumber || '',
        quoteByDate: formatDate(rfqData.quoteByDate),
        requestedDeliveryDate: formatDate(rfqData.requestedDeliveryDate),
        buyerDetails: {
          name: rfqData.buyerCompanyName || '',
          address: rfqData.buyerAddress || '',
          contact: rfqData.buyerContact || '',
          phone: rfqData.buyerPhone || '',
          email: rfqData.buyerEmail || '',
        },
        supplierDetails: {
          name: rfqData.supplierCompanyName || '',
          address: rfqData.supplierAddress || '',
          contact: rfqData.supplierName || '',
          phone: rfqData.supplierPhone || '',
          email: rfqData.supplierEmail || '',
        },
        freightForwarderDetails: {
          PIC: rfqData.freightForwarderPic || '',
          phone: rfqData.freightForwarderPhone || '',
          fax: rfqData.freightForwarderFax || '',
          email: rfqData.freightForwarderEmail || '',
          address: rfqData.freightForwarderAddress || '',
        },
        portAgentDetails: {
          name: rfqData.portAgentName || '',
          phone: rfqData.portAgentPhone || '',
          fax: rfqData.portAgentFax || '',
          email: rfqData.portAgentEmail || '',
          contact: rfqData.portAgentContact || '',
        },
        portDetails: {
          portName: rfqData.portName || '',
          vesselETA: formatDate(rfqData.vesselEta),
          vesselETD: formatDate(rfqData.vesselEtd),
        },
        lineItems: lineItemData.map((item: any, index: number) => ({
          srNo: index + 1,
          serviceCode: item.serviceCode || item.serviceName || '',
          serviceDescription: item.serviceDescription || '',
          qty: item.quantity || '',
          uom: item.uom || '',
          buyerComments: item.buyerComments || '',
        })),
      };

      // Dynamic import to avoid adding exceljs to initial bundle
      const Excel = await import('exceljs');
      const fileSaverMod = await import('file-saver');
      const saveAs = (fileSaverMod as any).saveAs || (fileSaverMod as any).default;
      const workbook = new Excel.Workbook();
      const ws = workbook.addWorksheet('Service RFQ');

      // === TITLE ===
      ws.mergeCells('A1:G1');
      const title = ws.getCell('A1');
      title.value = 'Service Request For Quotation';
      title.font = { size: 18, bold: true, color: { argb: '1F4E78' } };
      title.alignment = { horizontal: 'center', vertical: 'middle' };
      ws.addRow([]);

      // === RFQ DETAILS HEADER ===
      const rfqHeader = ws.addRow(['SERVICE RFQ DETAILS']);
      rfqHeader.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'D9E1F2' } };
      rfqHeader.getCell(1).font = { bold: true };
      ws.addRow([]);

      // RFQ Info
      const rfqInfo = [
        ['RFQ Ref:', data.rfqRef, '', '', '', 'RFQ Date:', data.rfqDate],
        ['Preferred Currency:', data.preferredCurrency, '', '', '', 'Vessel Name:', data.vesselName],
        ['IMO Number:', data.imoNumber, '', '', '', 'Quote By Date:', data.quoteByDate],
        ['Requested Delivery Date:', data.requestedDeliveryDate],
      ];
      rfqInfo.forEach((r) => {
        const row = ws.addRow(r);
        row.eachCell((c: any) => (c.alignment = { vertical: 'middle' }));
      });
      ws.addRow([]);

      // === BUYER + SUPPLIER ===
      const bsHeader = ws.addRow(['Buyer Details', '', '', 'Supplier Details']);
      bsHeader.eachCell((c: any) => {
        c.font = { bold: true };
        c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2CC' } };
      });
      ws.addRow([]);

      const buyerEntries = Object.entries(data.buyerDetails).map(([key, value]) => [
        labelMap[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase()),
        value,
      ]);

      const supplierEntries = Object.entries(data.supplierDetails).map(([key, value]) => [
        labelMap[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase()),
        value,
      ]);

      const maxLen = Math.max(buyerEntries.length, supplierEntries.length);
      for (let i = 0; i < maxLen; i++) {
        const b = buyerEntries[i] || ['', ''];
        const s = supplierEntries[i] || ['', ''];
        ws.addRow([b[0], b[1], '', s[0], s[1]]);
      }
      ws.addRow([]);

      // === FREIGHT FORWARDER + PORT AGENT ===
      const ffHeader = ws.addRow(['Freight Forwarder Details', '', '', 'Port Agent Details']);
      ffHeader.eachCell((c: any) => {
        c.font = { bold: true };
        c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'E2EFDA' } };
      });
      ws.addRow([]);

      const ffEntries = Object.entries(data.freightForwarderDetails).map(([key, value]) => [
        labelMap[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase()),
        value,
      ]);

      const paEntries = Object.entries(data.portAgentDetails).map(([key, value]) => [
        labelMap[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase()),
        value,
      ]);

      const maxLen2 = Math.max(ffEntries.length, paEntries.length);
      for (let i = 0; i < maxLen2; i++) {
        const f = ffEntries[i] || ['', ''];
        const p = paEntries[i] || ['', ''];
        ws.addRow([f[0], f[1], '', p[0], p[1]]);
      }
      ws.addRow([]);

      // === PORT DETAILS ===
      const portHeader = ws.addRow(['Port Details']);
      portHeader.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FCE4D6' } };
      portHeader.getCell(1).font = { bold: true };
      ws.addRow([]);

      const portEntries = Object.entries(data.portDetails).map(([key, value]) => [
        labelMap[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase()),
        value,
      ]);
      portEntries.forEach((e) => ws.addRow(e));
      ws.addRow([]);

      // === LINE ITEMS ===
      const lineHeader = ws.addRow(['SERVICE LINE ITEMS']);
      lineHeader.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'DDEBF7' } };
      lineHeader.getCell(1).font = { bold: true };
      ws.addRow([]);

      // Add header row for line items
      const itemHeader = ws.addRow(['Sr.No', 'Service Code', 'Service Description', 'Qty', 'UOM', 'Buyer Comments']);
      itemHeader.eachCell((c: any) => {
        c.font = { bold: true };
        c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'BDD7EE' } };
        c.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        };
      });

      // Add line item rows
      data.lineItems.forEach((item: any) => {
        const row = ws.addRow([
          item.srNo,
          item.serviceCode,
          item.serviceDescription,
          item.qty,
          item.uom,
          item.buyerComments,
        ]);
        row.eachCell((c: any) => {
          c.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' },
          };
        });
      });

      // Set column widths
      ws.columns = [
        { width: 20 },
        { width: 25 },
        { width: 35 },
        { width: 10 },
        { width: 10 },
        { width: 30 },
        { width: 20 },
      ];

      // Generate and download the file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      saveAs(blob, fileName);

      this.snack.open('Excel downloaded successfully.', 'OK', { duration: 3000 });
    } catch (error) {
      console.error('Error generating Service RFQ Excel:', error);
      this.snack.open('Error generating Excel. Please try again.', 'OK', { duration: 3000 });
    } finally {
      this.fullLoaderService.hideLoader()
      
    }
  }
}
